/**
 * "Show me that node" — the one thing a shop card needs to say to the research
 * tree.
 *
 * A hull or turret a player cannot buy yet names the node that would unlock it,
 * and that name is the most useful link in the garage: it is the exact question
 * *where do I go to fix this*. Making it clickable means one screen has to tell
 * another which of thirty-four nodes to point at.
 *
 * ── Why a module and not a prop or a route ───────────────────────────────────
 * The two screens are sibling tabs of `GarageScreen`, and only one is mounted at
 * a time — so there is no parent to thread a prop through that would not amount
 * to this file with extra steps. A query parameter would be the other option and
 * is worse here: the garage has no router, and a URL that survives a reload would
 * make the highlight outlive the intent behind it, which is precisely what the
 * reset rules below exist to prevent.
 *
 * ── It is a *nudge*, not a selection ─────────────────────────────────────────
 * Nothing here is state the tree depends on. The tree renders identically with
 * or without a focus; this only decides whether one card is ringed and scrolled
 * to. That is deliberate — a highlight that were load-bearing would be another
 * thing to keep in step with the catalogue, and the tree already derives
 * everything it draws from the server's own node list.
 *
 * ── When it clears ───────────────────────────────────────────────────────────
 * Both rules exist because a highlight is an answer to a question that has now
 * been asked, and an answer left lying around is noise:
 *
 *   **Leaving the tree clears it.** Otherwise a player who wandered off and came
 *   back a minute later would find a node ringed with no memory of asking, and —
 *   worse — the *wrong* node ringed if they had since looked at a different card.
 *
 *   **Starting the node clears it.** The moment the research is paid for, the
 *   thing the highlight was pointing at has happened. Leaving it lit would mark
 *   a finished node as the one still needing attention.
 */
import { computed, ref } from 'vue';

const focused = ref<string | null>(null);

/** The node the tree should ring and scroll to, or `null` for none. */
export const researchFocus = computed(() => focused.value);

/**
 * Asks the research tree to point at a node.
 *
 * The id is not validated against the catalogue, on purpose: the tree looks it
 * up in the node list it already has, and an id that matches nothing simply
 * highlights nothing. Checking here would mean this module holding a second
 * opinion about which nodes exist.
 */
export function focusResearch(nodeId: string): void {
  focused.value = nodeId;
}

/** Drops the highlight. Called when the tree unmounts and when a node starts. */
export function clearResearchFocus(): void {
  focused.value = null;
}

/**
 * Clears the highlight only if it is pointing at this node.
 *
 * For the "started it" rule. A blanket clear would be wrong the moment a player
 * follows a link to node A and then starts node B instead — B finishing is not
 * an answer to the question they asked about A, and the link they followed
 * should still be lit when they look back at it.
 */
export function clearResearchFocusIf(nodeId: string): void {
  if (focused.value === nodeId) focused.value = null;
}
